import Analytics from '@/utils/analytics';
import React, { useEffect, useRef } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { InboundDetailListItemAction } from './inbound-detail-list-item-action';
import { InboundDetailListItemCorrectionAction } from './inbound-detail-list-item-correction-action';
import { InboundDetailListItemHeader } from './inbound-detail-list-item-header';
import { InboundDetailOrderItem } from './inbound-detail-order-item';
import { InboundListItem, OrderAgentWorkflowMode } from './types';
import { InboundOrderableItemListItem } from './inbound-orderable-item-list-item';
import { InboundRenderActions } from './inbound-render-actions';
import { InboundUnmatchedCarousel } from './inbound-unmatched-carousel';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { LIST_MARGIN_HORIZONTAL, SCREEN_PADDING_SMALL, shadow } from '@/styles';
import {
  Platform,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { SwipeableWithInstantClose } from '@/components/list-item';
import { UpdateQuantityData } from '@/contexts/new-order-actions-context-types';
import { useHover } from 'react-native-web-hooks';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOrderInboundDetailStore } from './store/order-inbound-detail-store';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { VariantPackWithQuantities } from '@/hooks/use-order-inbound-reducer';

export const MIN_POPOVER_HEIGHT = 240;
export const PANEL_HEIGHT_OFFSET = 208;
export const PANEL_HEIGHT_OFFSET_TABLET = 168;

export interface PendingRemovalData {
  itemToRemove: InboundListItem;
  updateQuantityData: UpdateQuantityData;
}

interface Props {
  carouselItemWidth: number;
  correctionPreview?: ItemListOptionMetadata | null;
  inboundListItem: InboundListItem;
  inDraggableSheet?: boolean;
  isOrderOpen: boolean;
  listWidth: number | null;
  navigateToCorrection: (item: InboundListItem) => void;
  navigateToSearch: (item: InboundListItem) => void;
  onSwapItem: (
    item: InboundListItem,
    sku: ItemListOptionMetadata,
    onComplete?: () => void,
  ) => void;
  popoverContent: React.ReactNode;
  restaurantUUID: string;
  setPendingRemoval: React.Dispatch<
    React.SetStateAction<PendingRemovalData | null>
  >;
  showItemListView: boolean;
  showTranslation?: boolean;
  supplierUUID: string;
  updateItemNotes: (searchInputUUID: string, itemNotes: string | null) => void;
  updateQuantity: (searchInputUUID: string, quantity: number) => void;
  variantPackWithQuantities: VariantPackWithQuantities;
  workflowMode: OrderAgentWorkflowMode;
}

export const InboundDetailListItem: React.FC<Props> = ({
  carouselItemWidth,
  correctionPreview,
  inboundListItem,
  inDraggableSheet,
  isOrderOpen,
  listWidth,
  showItemListView,
  showTranslation,
  navigateToCorrection,
  navigateToSearch,
  onSwapItem,
  variantPackWithQuantities,
  restaurantUUID,
  setPendingRemoval,
  supplierUUID,
  updateItemNotes,
  updateQuantity,
  popoverContent,
  workflowMode,
}) => {
  const [styles] = useStyles();
  const isCorrectionMode = workflowMode === OrderAgentWorkflowMode.Correction;
  const { selectItemListOption, closeDetailSheet } = useItemDetailContext();
  const isInboundHistoryEnabled = useSupplierDynamicConfigFlag(
    'smart_order_config',
    'inbound_history_enabled',
  );
  const setSelectedInputUUID = useOrderInboundDetailStore(
    (state) => state.actions.setSelectedInputUUID,
  );
  const inboundListItemMetadata = inboundListItem.metadata;
  const inputUUID = inboundListItemMetadata?.inputWithResult.uuid;
  const touchableRef =
    useRef<React.ComponentRef<typeof TouchableOpacity>>(null);
  const swipeableRef = useRef<SwipeableWithInstantClose>(null);
  const isHovered = useHover(touchableRef);
  const windowDimensions = useWindowDimensions();
  const { isLargeLayout } = useLayoutContext();
  const offset = isLargeLayout
    ? PANEL_HEIGHT_OFFSET
    : PANEL_HEIGHT_OFFSET_TABLET;
  const height = Math.max(windowDimensions.height - offset, MIN_POPOVER_HEIGHT);

  useEffect(() => {
    if (!isInboundHistoryEnabled) {
      return;
    }

    if (!isHovered || !inputUUID) {
      return;
    }

    setSelectedInputUUID(inputUUID);

    return () => {
      if (
        useOrderInboundDetailStore.getState().selectedInputUUID === inputUUID
      ) {
        setSelectedInputUUID(undefined);
      }
    };
  }, [isHovered, inputUUID, setSelectedInputUUID, isInboundHistoryEnabled]);

  if (!inboundListItemMetadata) {
    return null;
  }
  const itemListOption = inboundListItemMetadata.listOption;

  const header = (
    <InboundDetailListItemHeader
      inboundListItem={inboundListItem}
      showTranslation={showTranslation}
    >
      {showItemListView || popoverContent ? null : (
        <InboundDetailListItemAction
          disabled={isCorrectionMode}
          inboundListItem={inboundListItem}
          navigateToSearch={navigateToSearch}
        />
      )}
    </InboundDetailListItemHeader>
  );

  const popover = popoverContent ? (
    <View
      style={[
        styles.popoverContainer,
        { height },
        !isLargeLayout ? styles.popoverContainerTablet : null,
      ]}
    >
      {popoverContent}
    </View>
  ) : null;

  if (itemListOption) {
    if (popover) {
      return (
        <>
          {header}
          {popover}
        </>
      );
    }

    const onPressItem = () => {
      selectItemListOption(
        itemListOption,
        {
          source: 'order_inbound_detail',
        },
        {
          renderActions: (sku) => (
            <InboundRenderActions
              isExistingVariantPack={
                itemListOption.metadata!.variantPackUUID === sku.variantPackUUID
              }
              onSelect={() => {
                onSwapItem(inboundListItem, sku, () => {
                  closeDetailSheet();
                });
              }}
            />
          ),
        },
      );
    };

    if (isInboundHistoryEnabled) {
      const orderItem = (
        <TouchableOpacity
          disabled={isCorrectionMode}
          onPress={onPressItem}
          ref={touchableRef}
        >
          <InboundDetailOrderItem
            correctionPreview={correctionPreview}
            inboundListItem={inboundListItem}
            inDraggableSheet={inDraggableSheet}
            isHovered={isHovered}
            isOrderOpen={isOrderOpen}
            key={inboundListItemMetadata.inputWithResult.result?.uuid}
            navigateToCorrection={navigateToCorrection}
            navigateToSearch={navigateToSearch}
            onChange={(previousQuantity, newQuantity) => {
              const smartOrderResult = inboundListItemMetadata.inputWithResult;

              Analytics.track('order_inbound_detail.update_quantity', {
                newQuantity,
                previousQuantity,
                smartOrderSearchInputUUID: smartOrderResult.uuid,
                smartOrderUUID: smartOrderResult.result?.smart_order_uuid,
              });

              updateQuantity(smartOrderResult.uuid, newQuantity);
              if (newQuantity === 0) {
                setPendingRemoval({
                  itemToRemove: inboundListItem,
                  updateQuantityData: {
                    eventPayloads: null,
                    newQuantity,
                    previousQuantity,
                    variantPackDetails: {
                      itemDisplayName: itemListOption.metadata!.displayName,
                      metadata: itemListOption.metadata!.variantPackMetadata,
                    },
                    variantPackUUID: itemListOption.metadata!.variantPackUUID,
                  },
                });
              }
            }}
            option={itemListOption}
            restaurantUUID={restaurantUUID}
            smartOrderInputUUID={
              inboundListItemMetadata.inputWithResult.result?.uuid || null
            }
            updateItemNotes={updateItemNotes}
            variantPackWithQuantities={variantPackWithQuantities}
            workflowMode={workflowMode}
          />
        </TouchableOpacity>
      );

      if (Platform.OS === 'web') {
        return orderItem;
      }

      return (
        <SwipeableWithInstantClose
          ref={swipeableRef}
          renderRightActions={() => (
            <View style={styles.swipeActionContainer}>
              {isCorrectionMode ? (
                <InboundDetailListItemCorrectionAction
                  inboundListItem={inboundListItem}
                  navigateToCorrection={(item) => {
                    navigateToCorrection(item);
                    swipeableRef.current?.close();
                  }}
                />
              ) : (
                <InboundDetailListItemAction
                  inboundListItem={inboundListItem}
                  navigateToSearch={(item) => {
                    navigateToSearch(item);
                    swipeableRef.current?.close();
                  }}
                />
              )}
            </View>
          )}
        >
          {orderItem}
        </SwipeableWithInstantClose>
      );
    }

    return (
      <>
        {header}
        <TouchableOpacity disabled={isCorrectionMode} onPress={onPressItem}>
          <InboundOrderableItemListItem
            action={
              showItemListView ? (
                <InboundDetailListItemAction
                  disabled={isCorrectionMode}
                  inboundListItem={inboundListItem}
                  navigateToSearch={navigateToSearch}
                />
              ) : null
            }
            containerStyle={styles.listItemContainer}
            hideSampleRequest
            inDraggableSheet={inDraggableSheet}
            isFocused={false}
            isInboundOrder
            option={itemListOption}
            restaurantUUID={restaurantUUID}
            showListView={showItemListView}
            showTooltipOnMount
            showVariantPackOrderInfo
            smartOrderInputUUID={
              inboundListItemMetadata.inputWithResult.result?.uuid || null
            }
            source="order_inbound_detail"
            updateQuantity={(updateQuantityPayload) => {
              const smartOrderResult = inboundListItemMetadata.inputWithResult;

              Analytics.track('order_inbound_detail.update_quantity', {
                ...updateQuantityPayload,
                smartOrderSearchInputUUID: smartOrderResult.uuid,
                smartOrderUUID: smartOrderResult.result?.smart_order_uuid,
              });

              updateQuantity(
                smartOrderResult.uuid,
                updateQuantityPayload.newQuantity,
              );
              if (updateQuantityPayload.newQuantity === 0) {
                setPendingRemoval({
                  itemToRemove: inboundListItem,
                  updateQuantityData: updateQuantityPayload,
                });
              }
            }}
            variantPackWithQuantities={variantPackWithQuantities}
            workflowMode={workflowMode}
          />
        </TouchableOpacity>
      </>
    );
  }

  return popover ? (
    <>
      {header}
      {popover}
    </>
  ) : (
    <InboundUnmatchedCarousel
      carouselItemWidth={carouselItemWidth}
      inboundListItem={inboundListItem}
      inDraggableSheet={inDraggableSheet}
      listWidth={listWidth}
      navigateToSearch={navigateToSearch}
      onSwapItem={(nextItem) => {
        onSwapItem(inboundListItem, nextItem);
      }}
      restaurantUUID={restaurantUUID}
      showTranslation={showTranslation}
      supplierUUID={supplierUUID}
    />
  );
};

const useStyles = createStyles((theme) => ({
  listItemContainer: {
    marginHorizontal: 0,
  },
  popoverContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: 8,
    ...shadow({
      color: theme.colors.black,
      elevation: 5,
      opacity: 0.08,
      radius: 24,
      x: 0,
      y: 12,
    }),
    marginBottom: SCREEN_PADDING_SMALL,
    marginHorizontal: LIST_MARGIN_HORIZONTAL,
    minHeight: MIN_POPOVER_HEIGHT,
    paddingTop: SCREEN_PADDING_SMALL,
  },
  popoverContainerTablet: {
    marginBottom: 96,
  },
  swipeActionContainer: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.secondary.default,
    justifyContent: 'center',
    width: 76,
  },
}));
